import React, { useEffect, useState } from 'react';
import { useAppStore } from '@/store';
import { useTheme } from '@/components/ThemeProvider';
import { UpdateProfileData } from '@/types';
import { Loader2, User, Phone, Building2, GraduationCap, Calendar, Save, Edit } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { cleanProfileData, mapProgramToFrontend, mapPTPhaseToFrontend, PT_PHASE_MAPPING } from '@/utils/profileMapping';
import { CollegeProgramSelector } from '@/components/CollegeProgramSelector';
import { getRegistrationProfile, clearRegistrationProfile } from '@/utils/registrationStorage';
import { useToastContext } from '@/contexts/ToastContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';

export const ProfileDetailsPage: React.FC = () => {
  const { profile, updateProfile, loading, fetchProfile, user } = useAppStore();
  const { theme } = useTheme();
  const { showSuccess, showError, showInfo } = useToastContext();
  const [isEditing, setIsEditing] = useState(false);
  const [localNames, setLocalNames] = useState<{firstName: string, lastName: string} | null>(null);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm<UpdateProfileData>();

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      let firstName = localNames?.firstName || user?.first_name || '';
      let lastName = localNames?.lastName || user?.last_name || '';

      if (!firstName && profile?.user_details?.full_name) {
        const fullNameParts = profile.user_details.full_name.trim().split(' ').filter(part => part.length > 0);
        if (fullNameParts.length > 0) {
          firstName = fullNameParts[0];
          lastName = fullNameParts.slice(1).join(' ') || '';
        }
      }

      if (!firstName && profile?.user_details?.username) {
        firstName = profile.user_details.username.split('.')[0] || 'Not Set';
      }
      if (!lastName && profile?.user_details?.username) {
        const usernameParts = profile.user_details.username.split('.');
        lastName = usernameParts.length > 1 ? usernameParts[1] : 'Not Set';
      }

      const formData = {
        first_name: firstName || 'Not Set',
        last_name: lastName || 'Not Set',
        program: mapProgramToFrontend(profile.program) || 'Not Set',
        year_of_study: profile.year_of_study || 1,
        pt_phase: mapPTPhaseToFrontend(profile.pt_phase) || 'PT1',
        department: profile.department || 'Not Set',
        phone_number: profile.phone_number || 'Not Set',
        company_name: profile.company_name || 'Not Set',
        company_region: profile.company_region || 'Not Set',
      };

      reset(formData);

      const isProfileIncomplete = !profile.program || !profile.year_of_study || !profile.pt_phase || !profile.department || !user?.first_name || !user?.last_name;

      if (isProfileIncomplete) {
        const storedProfileData = getRegistrationProfile();
        if (storedProfileData) {
          const cleanedData = cleanProfileData(storedProfileData);
          updateProfile(cleanedData).then(() => {
            clearRegistrationProfile();
            fetchProfile();
            showSuccess('Your profile has been automatically completed with your registration information!');
          }).catch(() => {
            // Silently handle error
          });
        }
      }
    }
  }, [profile, user, localNames, reset]);

  const onSubmit = async (data: UpdateProfileData) => {
    try {
      setIsUpdatingProfile(true);

      if (data.first_name || data.last_name) {
        setLocalNames({
          firstName: data.first_name || '',
          lastName: data.last_name || ''
        });
      }

      const cleanedData = cleanProfileData(data);
      const updatePromise = updateProfile(cleanedData);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout - server is taking too long to respond')), 20000)
      );

      await Promise.race([updatePromise, timeoutPromise]);
      setIsEditing(false);
      showSuccess('Profile updated successfully!');
      await fetchProfile();
    } catch (error: any) {
      setLocalNames(null);
      let errorMessage = 'Failed to update profile. Please try again.';

      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        errorMessage = 'Profile update timed out. Please try again in a few minutes.';
      } else if (error?.response?.status === 401) {
        errorMessage = 'Your session has expired. Please login again.';
      } else if (error?.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error?.response?.status === 400) {
        errorMessage = 'Invalid profile data. Please check your information and try again.';
      }

      showError(errorMessage);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  if (loading.isLoading && !profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
        <LoadingSpinner size="lg" color="primary" message="Loading your profile..." />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
        <User className={`w-12 h-12 text-${theme}-500 mb-4`} />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile Not Found</h2>
        <p className="text-gray-600 text-center">Unable to load your profile. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-orange-600">Profile Details</h1>
          <p className="text-gray-600">Manage your personal information and training details</p>
        </div>
        <button className="btn-outline flex items-center gap-2" onClick={() => setIsEditing(!isEditing)}>
          <Edit className="w-4 h-4" />
          {isEditing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {isEditing && (
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button type="submit" className="btn-primary flex items-center gap-2" disabled={isSubmitting || loading.isLoading || isUpdatingProfile}>
                {(isSubmitting || loading.isLoading || isUpdatingProfile) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isUpdatingProfile ? 'Updating...' : 'Save'}
              </button>
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><User className="w-5 h-5" />Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">First Name</label>
                <input className="input-field" disabled={!isEditing} {...register('first_name', { required: 'First name is required' })} />
                {errors.first_name && <p className="text-xs text-red-500 mt-1">{errors.first_name.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Last Name</label>
                <input className="input-field" disabled={!isEditing} {...register('last_name', { required: 'Last name is required' })} />
                {errors.last_name && <p className="text-xs text-red-500 mt-1">{errors.last_name.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Program</label>
                {isEditing ? (
                  <CollegeProgramSelector value={watch('program') || ''} onChange={(value) => setValue('program', value)} error={errors.program?.message} required={false} />
                ) : (
                  <div className="input-field bg-gray-100 cursor-not-allowed">{watch('program') || 'No program selected'}</div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Year of Study</label>
                <select className="input-field" disabled={!isEditing} {...register('year_of_study')}>
                  <option value="">Select year</option>
                  {[1, 2, 3, 4].map(year => <option key={year} value={year}>Year {year}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">PT Phase</label>
                <select className="input-field" disabled={!isEditing} {...register('pt_phase')}>
                  <option value="">Select PT phase</option>
                  {Object.entries(PT_PHASE_MAPPING).filter(([key]) => key.includes('Practical Training')).map(([displayName, value]) => (
                    <option key={value} value={displayName}>{displayName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Department</label>
                <input className="input-field" disabled={!isEditing} {...register('department')} />
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Phone className="w-5 h-5" />Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Phone Number</label>
                <input className="input-field bg-gray-100 cursor-not-allowed" disabled={true} {...register('phone_number')} />
                <p className="text-xs text-gray-500 mt-1">Phone number cannot be changed after registration</p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Building2 className="w-5 h-5" />Company Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Company Name</label>
                <input className="input-field" disabled={!isEditing} {...register('company_name')} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Company Region</label>
                <input className="input-field" disabled={!isEditing} {...register('company_region')} />
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="mt-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">Profile Completion</h3>
          <div className="space-y-3">
            {[
              { field: 'first_name', label: 'First Name', icon: User, value: watch('first_name') || localNames?.firstName || user?.first_name || profile?.user_details?.full_name?.split(' ')[0] },
              { field: 'last_name', label: 'Last Name', icon: User, value: watch('last_name') || localNames?.lastName || user?.last_name || profile?.user_details?.full_name?.split(' ').slice(1).join(' ') },
              { field: 'program', label: 'Program', icon: GraduationCap },
              { field: 'year_of_study', label: 'Year of Study', icon: Calendar },
              { field: 'pt_phase', label: 'PT Phase', icon: Calendar },
              { field: 'department', label: 'Department', icon: Building2 },
              { field: 'phone_number', label: 'Phone Number', icon: Phone },
              { field: 'company_name', label: 'Company Name', icon: Building2 },
              { field: 'company_region', label: 'Company Region', icon: Building2 },
            ].map(({ field, label, icon: Icon, value: customValue }) => {
              const value = customValue || profile[field as keyof typeof profile];
              const isComplete = value && value.toString().trim() !== '';
              return (
                <div key={field} className="flex items-center gap-3">
                  <Icon className={`w-4 h-4 ${isComplete ? 'text-green-500' : 'text-gray-400'}`} />
                  <span className="flex-1 text-sm text-gray-700">{label}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${isComplete ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {isComplete ? 'Complete' : 'Incomplete'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
